import { useVenueQueue } from "@/hooks/useVenueQueue";
import { useToast } from "@/components/ToastProvider";
import { Loader2, RefreshCw, Check, X, MapPin } from "lucide-react";
import { Fragment, useState } from "react";
import { normalizeUnknownValue } from "@/lib/utils";

export default function VenueQueuePage() {
  const { items, loading, error, load, approve, reject } = useVenueQueue();
  const { push } = useToast();
  const [limitInput, setLimitInput] = useState("100");

  async function handleApprove(id: string) {
    await approve(id);
    push({ title: "Venue approved", message: "Queued downstream tasks" });
  }

  async function handleDeny(id: string) {
    const reason = window.prompt("Why is this venue being denied?", "");
    await reject(id, reason ?? undefined);
    push({ title: "Venue denied", message: "Venue removed from queue" });
  }

  async function handleRefresh(customLimit?: number) {
    const limitValue = customLimit ?? Math.min(Math.max(parseInt(limitInput, 10) || 100, 1), 500);
    setLimitInput(String(limitValue));
    await load(limitValue);
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Venue Queue</h1>
          <p className="text-sm text-muted-foreground">
            Review pending venue submissions synced from knowledge stores.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-muted-foreground">
            Max rows
            <input
              type="number"
              min={1}
              max={500}
              value={limitInput}
              onChange={(event) => setLimitInput(event.target.value)}
              className="ml-2 w-20 rounded-md border border-border bg-background/40 px-2 py-1 text-sm text-foreground"
            />
          </label>
          <button
            onClick={() => {
              void handleRefresh();
            }}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="animate-spin" size={16} /> Loading pending venues…
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">
          Failed to load venues: {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="rounded-md border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">
          No venues are waiting for review.
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="space-y-3 md:hidden">
            {items.map((item) => {
              const venueName = normalizeUnknownValue(item.name) ?? "Unnamed venue";
              const city = normalizeUnknownValue(item.city);
              const state = normalizeUnknownValue(item.state);
              const country = normalizeUnknownValue(item.country);
              const locationParts = [city, state, country].filter(Boolean) as string[];
              return (
                <div key={item.id} className="rounded-2xl border border-border bg-background/40 p-4 space-y-3">
                  <div>
                    <p className="font-semibold text-foreground">{venueName}</p>
                    <p className="text-xs text-muted-foreground">{item.id}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground/70">
                    <MapPin size={14} className="text-primary" />
                    <span>{locationParts.length ? locationParts.join(", ") : "Unknown"}</span>
                  </div>
                  <p className="text-xs text-foreground/70">
                    Submitted: {item.submitted_at ? new Date(item.submitted_at).toLocaleString() : "—"}
                  </p>
                  <p className="text-xs text-foreground/70">
                    Notes: {item.notes ||
                      (typeof item.metadata["notes"] === "string"
                        ? (item.metadata["notes"] as string)
                        : "—")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleApprove(item.id)}
                      className="inline-flex min-h-[44px] md:min-h-[36px] items-center gap-1 rounded-md border border-emerald-500/40 px-3 py-2 text-xs font-medium text-emerald-200 hover:bg-emerald-500/10"
                    >
                      <Check size={14} /> Approve
                    </button>
                    <button
                      onClick={() => handleDeny(item.id)}
                      className="inline-flex min-h-[44px] md:min-h-[36px] items-center gap-1 rounded-md border border-red-500/40 px-3 py-2 text-xs font-medium text-red-200 hover:bg-red-500/10"
                    >
                      <X size={14} /> Deny
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="hidden md:block overflow-x-auto rounded-lg border border-border bg-background/40">
            <table className="min-w-full divide-y divide-gray-800">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Venue</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/70 text-sm">
              {items.map((item) => {
                const venueName = normalizeUnknownValue(item.name) ?? "Unnamed venue";
                const city = normalizeUnknownValue(item.city);
                const state = normalizeUnknownValue(item.state);
                const country = normalizeUnknownValue(item.country);
                const locationParts = [city, state, country].filter(Boolean) as string[];
                return (
                  <tr key={item.id} className="hover:bg-muted/40">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{venueName}</div>
                      <div className="text-xs text-muted-foreground">{item.id}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground/70">
                      <div className="flex items-center gap-1">
                        <MapPin size={14} className="text-primary" />
                        <span>{locationParts.length ? locationParts.join(", ") : "Unknown"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground/70">
                      {item.submitted_at ? new Date(item.submitted_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground/70">
                      {item.notes ||
                        (typeof item.metadata["notes"] === "string"
                          ? (item.metadata["notes"] as string)
                          : "—")}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleApprove(item.id)}
                          className="inline-flex min-h-[44px] md:min-h-[36px] items-center gap-1 rounded-md border border-emerald-500/40 px-3 py-2 text-xs font-medium text-emerald-200 hover:bg-emerald-500/10"
                        >
                          <Check size={14} /> Approve
                        </button>
                        <button
                          onClick={() => handleDeny(item.id)}
                          className="inline-flex min-h-[44px] md:min-h-[36px] items-center gap-1 rounded-md border border-red-500/40 px-3 py-2 text-xs font-medium text-red-200 hover:bg-red-500/10"
                        >
                          <X size={14} /> Deny
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
        </>
      )}

      {items.some((item) => item.document) && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Source documents</h2>
          <div className="grid gap-3">
            {items.map((item) => (
              <Fragment key={`${item.id}-doc`}>
                {item.document && (
                  <article className="rounded-md border border-border bg-background/40 p-4 text-sm text-foreground/80">
                    <header className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                      {item.name ?? item.id}
                    </header>
                    <p className="whitespace-pre-wrap text-foreground/70">{item.document}</p>
                  </article>
                )}
              </Fragment>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
